/**
 * PaywallModal Component
 *
 * Modal de paywall com 4 planos usando decoy effect.
 * Plano Mensal destacado como "MAIS POPULAR".
 * Design dark matching FeedPage.
 */

import { useState } from 'react'
import CheckoutPIX from './CheckoutPIX'

// ========================================
// TYPES
// ========================================

interface PaywallModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface Plan {
  id: string
  name: string
  price: string
  pricePerDay: string
  period: string
  tag?: string
  highlighted?: boolean
}

// ========================================
// PLANS (Decoy Effect)
// ========================================

const PLANS: Plan[] = [
  {
    id: 'weekly',
    name: 'Semanal',
    price: 'R$ 29,90',
    pricePerDay: 'R$ 4,27/dia',
    period: '7 dias',
  },
  {
    id: 'monthly',
    name: 'Mensal',
    price: 'R$ 79,90',
    pricePerDay: 'R$ 2,66/dia',
    period: '30 dias',
    tag: 'MAIS POPULAR',
    highlighted: true,
  },
  {
    id: 'quarterly',
    name: 'Trimestral',
    price: 'R$ 199,90',
    pricePerDay: 'R$ 2,22/dia',
    period: '90 dias',
  },
  {
    id: 'semester',
    name: 'Semestral',
    price: 'R$ 349,90',
    pricePerDay: 'R$ 1,94/dia',
    period: '180 dias',
  },
]

// ========================================
// COMPONENT
// ========================================

export default function PaywallModal({ onClose, onSuccess }: PaywallModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<string>('monthly')
  const [showCheckout, setShowCheckout] = useState(false)

  const handleSubscribe = () => {
    setShowCheckout(true)
  }

  const handleCheckoutSuccess = () => {
    onSuccess()
  }

  const handleCheckoutBack = () => {
    setShowCheckout(false)
  }

  // Se esta no fluxo de checkout, renderizar CheckoutPIX
  if (showCheckout) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <CheckoutPIX
            planId={selectedPlan}
            onSuccess={handleCheckoutSuccess}
            onBack={handleCheckoutBack}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">&#x1F680;</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-1">
              Desbloqueie todas as questoes
            </h2>
            <p className="text-sm text-white/50">
              Acesse todas as categorias e aumente suas chances de aprovacao
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="px-4 pb-4 space-y-2">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`
                relative w-full p-4 rounded-xl text-left transition-all flex items-center justify-between
                ${plan.highlighted && selectedPlan === plan.id
                  ? 'bg-green-500/15 border-2 border-green-500 shadow-lg shadow-green-500/10'
                  : selectedPlan === plan.id
                    ? 'bg-white/10 border-2 border-white/30'
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }
              `}
            >
              {/* Tag */}
              {plan.tag && (
                <span className="absolute -top-2.5 left-4 px-2 py-0.5 text-[10px] font-bold bg-green-500 text-black rounded-full uppercase tracking-wider">
                  {plan.tag}
                </span>
              )}

              {/* Left: Radio + Info */}
              <div className="flex items-center gap-3">
                <div
                  className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                    ${selectedPlan === plan.id
                      ? 'border-green-500 bg-green-500'
                      : 'border-white/30'
                    }
                  `}
                >
                  {selectedPlan === plan.id && (
                    <div className="w-2 h-2 rounded-full bg-black" />
                  )}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{plan.name}</div>
                  <div className="text-xs text-white/40">{plan.period} &middot; {plan.pricePerDay}</div>
                </div>
              </div>

              {/* Right: Price */}
              <div className="text-right">
                <div className={`text-lg font-bold ${plan.highlighted ? 'text-green-400' : 'text-white'}`}>
                  {plan.price}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="px-4 pb-6">
          <button
            onClick={handleSubscribe}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-cyan-500 text-black font-bold text-base hover:from-green-400 hover:to-cyan-400 transition-all shadow-lg shadow-green-500/20"
          >
            Assinar com PIX
          </button>
          <p className="text-center text-xs text-white/30 mt-3">
            Pagamento seguro via PIX &middot; Acesso imediato
          </p>
        </div>
      </div>
    </div>
  )
}
